import { Plus } from 'lucide-react';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter,
} from '@/components/ui/drawer';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useLang } from '@/context/LanguageContext';

export const FAB = ({ onClick, testId, label = 'Add' }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    aria-label={label}
    className="btn-brand fixed bottom-24 left-1/2 z-40 ml-[148px] flex h-14 w-14 -translate-x-1/2 items-center justify-center rounded-full text-brand-ink shadow-[var(--shadow-floating)]"
  >
    <Plus className="h-6 w-6" />
  </button>
);

const inputClass =
  'h-11 rounded-xl border-white/10 bg-white/[0.06] text-foreground placeholder:text-foreground/40';

export const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-foreground/70">{label}</Label>
    {children}
  </div>
);

export const TextField = ({ label, testId, ...props }) => (
  <Field label={label}>
    <Input data-testid={testId} className={inputClass} {...props} />
  </Field>
);

export const TextAreaField = ({ label, testId, ...props }) => (
  <Field label={label}>
    <Textarea
      data-testid={testId}
      className="min-h-[80px] rounded-xl border-white/10 bg-white/[0.06] text-foreground placeholder:text-foreground/40"
      {...props}
    />
  </Field>
);

// Options are canonical English enum VALUES. We display localized labels via
// `tEnum` but keep the stored value untouched (filters/chips depend on it).
export const SelectField = ({ label, value, onValueChange, options, testId }) => {
  const { t, tEnum } = useLang();
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger data-testid={testId} className={inputClass}>
          <SelectValue placeholder={t('common.select')} />
        </SelectTrigger>
        <SelectContent className="border-white/10 bg-[#0A1730] text-foreground">
          {options.map((opt) => (
            <SelectItem key={opt} value={opt} className="text-foreground focus:bg-white/[0.08]">
              {tEnum(opt)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
};

export const FormDrawer = ({ open, onOpenChange, title, children, onSubmit, submitLabel = 'Save', submitTestId, loading }) => (
  <Drawer open={open} onOpenChange={onOpenChange}>
    <DrawerContent className="mx-auto max-w-[480px] border-white/10 bg-[#0A1730] text-foreground">
      <DrawerHeader className="text-left">
        <DrawerTitle className="font-display text-lg font-semibold">{title}</DrawerTitle>
      </DrawerHeader>
      <div className="max-h-[62vh] space-y-4 overflow-y-auto px-4">{children}</div>
      <DrawerFooter>
        <Button
          data-testid={submitTestId}
          onClick={onSubmit}
          disabled={loading}
          className="btn-brand h-12 w-full rounded-xl text-base font-semibold text-brand-ink"
        >
          {submitLabel}
        </Button>
      </DrawerFooter>
    </DrawerContent>
  </Drawer>
);
